// Reports API Service
import HttpClient from '../http/HttpClient';
import { LEAD_SERVICE_HOST, LEAD_API_PREFIX } from '../constants/RestConstants';
import {
  MonthlySummaryResponse,
  ReportApiResponse
} from '../../models/ReportInterface';

/**
 * Get Monthly Summary Reports
 * GET /api/v1/reports/monthly-summary
 */
export async function getMonthlySummaryReports(): Promise<ReportApiResponse<MonthlySummaryResponse>> {
  const response = await HttpClient.GET({
    BASE_URL: LEAD_SERVICE_HOST,
    END_POINT: `${LEAD_API_PREFIX}/reports/monthly-summary`,
  });
  
  return response;
}

/**
 * All saved month-end KPI snapshots (oldest → newest in list).
 * GET /api/v1/reports/monthly-summary/all
 */
export async function getAllMonthlyKpiSnapshots(): Promise<{ data?: MonthlySummaryResponse }> {
  return HttpClient.GET({
    BASE_URL: LEAD_SERVICE_HOST,
    END_POINT: `${LEAD_API_PREFIX}/reports/monthly-summary/all`,
  });
}

/**
 * Generate Monthly Summary Report
 * POST /api/v1/reports/monthly-summary/generate
 */
export async function generateMonthlySummaryReport(): Promise<ReportApiResponse<MonthlySummaryResponse>> {
  const response = await HttpClient.POST({
    BASE_URL: LEAD_SERVICE_HOST,
    END_POINT: `${LEAD_API_PREFIX}/reports/monthly-summary/generate`,
    data: {},
  });
  
  return response;
}

/**
 * Get monthly summary data for dashboard
 */
export async function getMonthlySummaryData() {
  try {
    const response = await getMonthlySummaryReports();
    return response.data;
  } catch (error) {
    console.error('Error fetching monthly summary data:', error);
    throw error;
  }
}

/**
 * Generate monthly summary data on demand
 */
export async function generateMonthlySummaryData() {
  try {
    const response = await generateMonthlySummaryReport();
    return response.data;
  } catch (error) {
    console.error('Error generating monthly summary data:', error);
    throw error;
  }
}

/** Row for revenue & cashflow by customer report */
export interface CustomerRevenueCashflowRow {
  borrowerId: string;
  borrowerName: string;
  customerId: string;
  interestCollected: number;
  lateFeesCollected: number;
  revenue: number;
  principalCollected: number;
  totalDisbursed: number;
  cashInflow: number;
  netCashflow: number;
}

export interface RevenueCashflowReportResponse {
  reportDate: string;
  totalCustomers: number;
  totalRevenue: number;
  totalNetCashflow: number;
  rows: CustomerRevenueCashflowRow[];
  message: string;
  statusCode: number;
}

/**
 * Get revenue and cashflow by customer report
 * GET /api/v1/reports/revenue-cashflow
 */
export async function getRevenueCashflowReport(): Promise<{ data?: RevenueCashflowReportResponse }> {
  return HttpClient.GET({
    BASE_URL: LEAD_SERVICE_HOST,
    END_POINT: `${LEAD_API_PREFIX}/reports/revenue-cashflow`,
  });
}

/**
 * Send revenue/cashflow report to email
 * POST /api/v1/reports/revenue-cashflow/send-email
 * @param email Optional; if not provided uses backend default report email
 */
export async function sendRevenueCashflowReportEmail(email?: string): Promise<{ data?: { message?: string }; message?: string }> {
  const params = email ? { email } : {};
  return HttpClient.POST({
    BASE_URL: LEAD_SERVICE_HOST,
    END_POINT: `${LEAD_API_PREFIX}/reports/revenue-cashflow/send-email`,
    params,
    data: {},
  });
}

/** Row: interest collected in current month (till today) by customer + expected interest + last payment date */
export interface InterestCollectedByCustomerRow {
  borrowerId: string;
  borrowerName: string;
  customerId: string;
  interestCollectedCurrentMonth: number;
  expectedInterest: number;
  lastPaymentDate: string | null; // ISO instant
}

export interface InterestCollectedByCustomerResponse {
  reportDate: string;
  totalCustomers: number;
  totalInterestCollectedCurrentMonth: number;
  totalExpectedInterest: number;
  rows: InterestCollectedByCustomerRow[];
  message: string;
  statusCode: number;
}

/**
 * Get interest collected in current month (till today) by each customer, with last payment date.
 * GET /api/v1/reports/interest-collected-current-month
 */
export async function getInterestCollectedByCustomerCurrentMonth(): Promise<{ data?: InterestCollectedByCustomerResponse }> {
  return HttpClient.GET({
    BASE_URL: LEAD_SERVICE_HOST,
    END_POINT: `${LEAD_API_PREFIX}/reports/interest-collected-current-month`,
  });
}

export interface PaymentTransactionMonthlyItem {
  id: string;
  customerName: string;
  amount: number;
  paymentType: string;
  paymentTypeLabel: string;
  flow: 'GIVEN' | 'RECEIVED';
  createdAt: string | null;
}

export interface PaymentTransactionMonthlyBlock {
  year: number;
  month: number;
  monthLabel: string;
  transactionCount: number;
  totalAmountReceived: number;
  totalAmountGiven: number;
  netCashflow: number;
  totalMonthlyVolume: number;
  items: PaymentTransactionMonthlyItem[];
}

export interface PaymentTransactionsMonthlyResponse {
  reportDate: string;
  months: PaymentTransactionMonthlyBlock[];
  grandTotalTransactionCount: number;
  grandTotalAmountReceived: number;
  grandTotalAmountGiven: number;
  grandNetCashflow: number;
  message: string;
  statusCode: number;
}

/**
 * GET /api/v1/reports/payment-transactions-monthly
 * Payments on active debits, grouped by month (from first payment through current month); not filtered by payment status.
 */
export async function getPaymentTransactionsMonthly(): Promise<{ data?: PaymentTransactionsMonthlyResponse }> {
  return HttpClient.GET({
    BASE_URL: LEAD_SERVICE_HOST,
    END_POINT: `${LEAD_API_PREFIX}/reports/payment-transactions-monthly`,
  });
}

export interface CustomerLendingAuditRow {
  borrowerId: string;
  borrowerName: string;
  customerId: string;
  blacklisted: boolean;
  interestOutstanding: number;
  shortTermOutstanding: number;
  openInterestDebitCount: number;
  openShortTermDebitCount: number;
  expectedInterestMonthly: number;
}

export interface CustomerLendingAuditResponse {
  reportDate: string;
  customerCount: number;
  totalInterestOutstanding: number;
  totalShortTermOutstanding: number;
  totalExpectedInterestMonthly: number;
  rows: CustomerLendingAuditRow[];
  message: string;
  statusCode: number;
}

/** GET /api/v1/reports/customer-lending-audit */
export async function getCustomerLendingAudit(): Promise<{ data?: CustomerLendingAuditResponse }> {
  return HttpClient.GET({
    BASE_URL: LEAD_SERVICE_HOST,
    END_POINT: `${LEAD_API_PREFIX}/reports/customer-lending-audit`,
  });
}

export interface CapitalEfficiencySeedResponse {
  asOfDate: string;
  lendingCapital: number;
  lendingMonthlyInterestExpected: number;
  stocksInvested: number | null;
  stocksCurrentValue: number | null;
  stocksProfitLoss: number | null;
  mfInvested: number | null;
  mfCurrentValue: number | null;
  equityInvested: number | null;
  equityCurrentValue: number | null;
  message: string;
  statusCode: number;
}

/** GET /api/v1/reports/capital-efficiency-seed */
export async function getCapitalEfficiencySeed(): Promise<{ data?: CapitalEfficiencySeedResponse }> {
  return HttpClient.GET({
    BASE_URL: LEAD_SERVICE_HOST,
    END_POINT: `${LEAD_API_PREFIX}/reports/capital-efficiency-seed`,
  });
}

export interface CapitalEfficiencyManualEmailRequest {
  recipientEmail?: string;
  fyYear: string;
  capitalInvested: number;
  annualReturn: number;
  note?: string;
}

/** POST /api/v1/reports/capital-efficiency/manual-email */
export async function sendCapitalEfficiencyManualEmail(body: CapitalEfficiencyManualEmailRequest): Promise<{ data?: { message?: string; status?: number; statusCode?: number } }> {
  return HttpClient.POST({
    BASE_URL: LEAD_SERVICE_HOST,
    END_POINT: `${LEAD_API_PREFIX}/reports/capital-efficiency/manual-email`,
    data: body,
  });
}

export type RiskLevel = 'Low' | 'Medium' | 'High';
export type EffortLevel = 'Low' | 'Medium' | 'High';

export interface MonthlyCapitalEfficiencyUpsertRequest {
  assetId: string;
  assetType: string;
  month: string; // YYYY-MM
  startValue: number;
  endValue: number;
  capitalAdded?: number;
  capitalWithdrawn?: number;
  incomeGenerated?: number;
  expenses?: number;
  riskLevel?: RiskLevel;
  effortLevel?: EffortLevel;
}

export interface MonthlyCapitalEfficiencySnapshot {
  id: string;
  assetId: string;
  assetType: string;
  month: string;
  startValue: number;
  endValue: number;
  capitalAdded: number;
  capitalWithdrawn: number;
  incomeGenerated: number;
  expenses: number;
  riskLevel?: string | null;
  effortLevel?: string | null;
  createdBy?: string;
  createdDate?: string;
  updatedBy?: string;
  updatedDate?: string;
}

export interface MonthlyCapitalEfficiencyAssetRow {
  assetId: string;
  assetType: string;
  month: string;
  startValue: number;
  endValue: number;
  capitalAdded: number;
  capitalWithdrawn: number;
  incomeGenerated: number;
  expenses: number;
  netReturn: number;
  mcePct: number;
  annualizedCePct: number;
  riskLevel?: string | null;
  effortLevel?: string | null;
  lossMaking: boolean;
}

export interface MonthlyCapitalEfficiencyMonthBlock {
  month: string;
  totalStartCapital: number;
  totalEndCapital: number;
  totalNetReturn: number;
  portfolioMcePct: number;
  annualizedCePct: number;
  positiveReturnSharePct: number;
  rows: MonthlyCapitalEfficiencyAssetRow[];
  insights: string[];
  flags: string[];
}

export interface MonthlyCapitalEfficiencyDashboardResponse {
  asOfDate: string;
  fromMonth: string;
  toMonth: string;
  months: MonthlyCapitalEfficiencyMonthBlock[];
  message: string;
  statusCode: number;
}

/** POST /api/v1/reports/mce/snapshots */
export async function upsertMonthlyCapitalEfficiencySnapshot(
  body: MonthlyCapitalEfficiencyUpsertRequest
): Promise<{ data?: MonthlyCapitalEfficiencySnapshot }> {
  return HttpClient.POST({
    BASE_URL: LEAD_SERVICE_HOST,
    END_POINT: `${LEAD_API_PREFIX}/reports/mce/snapshots`,
    data: body,
  });
}

/** GET /api/v1/reports/mce/snapshots?fromMonth&toMonth */
export async function listMonthlyCapitalEfficiencySnapshots(fromMonth: string, toMonth: string): Promise<{ data?: MonthlyCapitalEfficiencySnapshot[] }> {
  return HttpClient.GET({
    BASE_URL: LEAD_SERVICE_HOST,
    END_POINT: `${LEAD_API_PREFIX}/reports/mce/snapshots`,
    params: { fromMonth, toMonth },
  });
}

/** GET /api/v1/reports/mce/dashboard?fromMonth&toMonth */
export async function getMonthlyCapitalEfficiencyDashboard(fromMonth: string, toMonth: string): Promise<{ data?: MonthlyCapitalEfficiencyDashboardResponse }> {
  return HttpClient.GET({
    BASE_URL: LEAD_SERVICE_HOST,
    END_POINT: `${LEAD_API_PREFIX}/reports/mce/dashboard`,
    params: { fromMonth, toMonth },
  });
}
